import {rotateCartesian} from "../../../utils/MathUtils";

class NativeEventHandler {
    constructor(
        portal,
        updateCamera,
        scaleOptions,
        localCameraRef,
        options = {}
    ) {
        this.portal = portal;
        this.updateCamera = updateCamera;
        this.scaleOptions = scaleOptions;
        this.localCamera = localCameraRef;

        this.panSpeedMultiplier = options.panSpeedMultiplier ?? 1.25;
        this.disableRotate = options.disableRotate ?? false;

        this.startPanCamera = null;
        this.startPanPoint = null;

        this.startDistance = null;
        this.startZoom = null;
        this.startRotate = null;
        this.startAngle = null;
        this.startCenter = null;

        this.isMousePanning = false;
        this.isTouchPanning = false;
        this.isPinching = false;

        this.initNativeGestureHandling();
    }

    /* ------------------------------------------------------ */
    /* INIT + DESTROY                                          */
    /* ------------------------------------------------------ */

    initNativeGestureHandling() {
        const el = this.portal?.current;
        if (!el) return;

        // Touch
        el.addEventListener("touchstart", this.onTouchStart, {passive: false});
        el.addEventListener("touchmove", this.onTouchMove, {passive: false});
        el.addEventListener("touchend", this.onTouchEnd, {passive: false});
        el.addEventListener("touchcancel", this.onTouchEnd, {passive: false});

        // Mouse
        el.addEventListener("mousedown", this.onMouseDown);
        el.addEventListener("wheel", this.onWheel, {passive: false});

        window.addEventListener("mousemove", this.onMouseMove);
        window.addEventListener("mouseup", this.onMouseUp);
    }

    destroy() {
        const el = this.portal?.current;
        if (el) {
            el.removeEventListener("touchstart", this.onTouchStart);
            el.removeEventListener("touchmove", this.onTouchMove);
            el.removeEventListener("touchend", this.onTouchEnd);
            el.removeEventListener("touchcancel", this.onTouchEnd);

            el.removeEventListener("mousedown", this.onMouseDown);
            el.removeEventListener("wheel", this.onWheel);
        }

        window.removeEventListener("mousemove", this.onMouseMove);
        window.removeEventListener("mouseup", this.onMouseUp);
    }

    /* ------------------------------------------------------ */
    /* TOUCH START                                              */
    /* ------------------------------------------------------ */

    onTouchStart = (e) => {
        if (e.touches.length === 1) {
            // e.preventDefault();
            // DO NOT use preventDefault -> allows double-click

            const t = e.touches[0];
            this.startPanCamera = {...this.localCamera()};
            this.startPanPoint = {x: t.clientX, y: t.clientY};

            this.isTouchPanning = true;
            this.isPinching = false;
        }

        if (e.touches.length === 2) {
            e.preventDefault();

            const [t1, t2] = [...e.touches];
            const dx = t2.clientX - t1.clientX;
            const dy = t2.clientY - t1.clientY;

            this.startDistance = Math.hypot(dx, dy);
            this.startAngle = Math.atan2(dy, dx);
            this.startCenter = this.getTouchMidpoint([t1, t2]);

            this.startPanCamera = {...this.localCamera()};
            this.startZoom = this.startPanCamera.zoom;
            this.startRotate = this.startPanCamera.rotate;

            this.isTouchPanning = false;
            this.isPinching = true;
        }
    };

    /* ------------------------------------------------------ */
    /* TOUCH MOVE                                               */
    /* ------------------------------------------------------ */

    onTouchMove = (e) => {
        if (
            e.touches.length === 1 &&
            this.isTouchPanning &&
            this.startPanCamera
        ) {
            e.preventDefault();

            const t = e.touches[0];
            const deltaX =
                (t.clientX - this.startPanPoint.x) * this.panSpeedMultiplier;
            const deltaY =
                (t.clientY - this.startPanPoint.y) * this.panSpeedMultiplier;

            const {zoom, rotate} = this.startPanCamera;
            const {x, y} = rotateCartesian(deltaX, deltaY, -rotate);

            this.updateCamera((cam) => ({
                ...cam,
                x: this.startPanCamera.x - x / zoom,
                y: this.startPanCamera.y - y / zoom,
            }));
        }

        if (e.touches.length === 2 && this.isPinching && this.startDistance) {
            e.preventDefault();

            const [t1, t2] = [...e.touches];
            const currCenter = this.getTouchMidpoint([t1, t2]);

            const dx = t2.clientX - t1.clientX;
            const dy = t2.clientY - t1.clientY;

            const currDistance = Math.hypot(dx, dy);
            const currAngle = Math.atan2(dy, dx);

            const zoomFactor = currDistance / this.startDistance;
            const panDx =
                (currCenter.x - this.startCenter.x) * this.panSpeedMultiplier;
            const panDy =
                (currCenter.y - this.startCenter.y) * this.panSpeedMultiplier;

            const newZoom = this.scaleOptions.setCurrent(
                this.startZoom * zoomFactor
            ).scale;

            let newRotate = this.startRotate;
            if (!this.disableRotate) {
                const angleDelta =
                    (currAngle - this.startAngle) * (180 / Math.PI);
                newRotate = this.startRotate + angleDelta;
            }

            const {x, y} = rotateCartesian(panDx, panDy, -newRotate);
            const newX = this.startPanCamera.x - x / newZoom;
            const newY = this.startPanCamera.y - y / newZoom;

            this.updateCamera((cam) => ({
                ...cam,
                x: newX,
                y: newY,
                zoom: newZoom,
                rotate: newRotate,
            }));
        }
    };

    /* ------------------------------------------------------ */
    /* TOUCH END                                                */
    /* ------------------------------------------------------ */

    onTouchEnd = (e) => {
        if (e.touches.length === 0) {
            this.isTouchPanning = false;
            this.isPinching = false;

            this.startPanCamera = null;
            this.startPanPoint = null;
            this.startDistance = null;
            this.startCenter = null;
            this.startZoom = null;
            this.startRotate = null;
            this.startAngle = null;
        }

        if (e.touches.length === 1) {
            const t = e.touches[0];

            this.startPanCamera = {...this.localCamera()};
            this.startPanPoint = {x: t.clientX, y: t.clientY};

            this.isTouchPanning = true;
            this.isPinching = false;
        }
    };

    /* ------------------------------------------------------ */
    /* MOUSE EVENTS                                             */
    /* ------------------------------------------------------ */

    onMouseDown = (e) => {
        if (e.button !== 0) return;

        // DO NOT use preventDefault -> allows double-click
        this.startPanCamera = {...this.localCamera()};
        this.startPanPoint = {x: e.clientX, y: e.clientY};
        this.isMousePanning = true;
    };

    onMouseMove = (e) => {
        if (!this.isMousePanning || !this.startPanCamera) return;

        const deltaX =
            (e.clientX - this.startPanPoint.x) * this.panSpeedMultiplier;
        const deltaY =
            (e.clientY - this.startPanPoint.y) * this.panSpeedMultiplier;

        const {zoom, rotate} = this.startPanCamera;
        const {x, y} = rotateCartesian(deltaX, deltaY, -rotate);

        this.updateCamera((cam) => ({
            ...cam,
            x: this.startPanCamera.x - x / zoom,
            y: this.startPanCamera.y - y / zoom,
        }));
    };

    onMouseUp = () => {
        this.isMousePanning = false;
        this.startPanCamera = null;
    };

    /* ------------------------------------------------------ */
    /* WHEEL ZOOM                                               */
    /* ------------------------------------------------------ */

    onWheel = (e) => {
        e.preventDefault();

        const cam = this.localCamera();
        const factor = e.deltaY < 0 ? 1.1 : 0.9;

        this.updateCamera(() => ({
            ...cam,
            zoom: this.scaleOptions.setCurrent(cam.zoom * factor).scale,
        }));
    };

    /* ------------------------------------------------------ */
    /* UTILS                                                    */
    /* ------------------------------------------------------ */

    getTouchMidpoint(pointers) {
        return {
            x: (pointers[0].clientX + pointers[1].clientX) / 2,
            y: (pointers[0].clientY + pointers[1].clientY) / 2,
        };
    }
}

export default NativeEventHandler;
